<div class="gallery-container">
  <h1>Pipes &amp; Utilities</h1>

  <!-- ===== PIPES ===== -->
  <h2 class="mt-4">Pipes</h2>
  <div>Path: <code class="component-path">src/lib/shared/pipes</code></div>

  <section class="component-section">
    <h2>Address Pipe</h2>
    <div>Path: <code class="component-path">./address.pipe.ts</code></div>
    <div>Pipe name: <code>address</code></div>
    <div>Usage: <code>address | address</code> or <code>address | address:'format string'</code></div>
    <div class="gallery-item">
      <p>Transforms an <code>Address</code> object into a formatted string using named tokens.</p>
      <h3>Available Tokens</h3>
      <table class="table">
        <thead><tr><th>Token</th><th>Maps to</th></tr></thead>
        <tbody>
        <tr><td><code>Attention</code></td><td><code>address.attention</code></td></tr>
        <tr><td><code>Address1</code></td><td><code>address.addressLine1</code></td></tr>
        <tr><td><code>Address2</code></td><td><code>address.addressLine2</code></td></tr>
        <tr><td><code>City</code></td><td><code>address.city</code></td></tr>
        <tr><td><code>StateCode</code></td><td><code>address.state.stateCode</code></td></tr>
        <tr><td><code>StateName</code></td><td><code>address.state.name</code></td></tr>
        <tr><td><code>PostalCode</code></td><td><code>address.postalCode</code></td></tr>
        <tr><td><code>CountryCode</code></td><td><code>address.country.countryCode</code></td></tr>
        <tr><td><code>CountryName</code></td><td><code>address.country.name</code></td></tr>
        </tbody>
      </table>
      <p>Wrap a segment in <code>[…]</code> to make it optional — it is omitted when all tokens inside it are empty.</p>
      <h3>Format Examples</h3>
      <table class="table">
        <thead><tr><th>Format string</th><th>Output (example address)</th></tr></thead>
        <tbody>
        <tr>
          <td><code>[addressLine1, ][addressLine2, ][city, ][stateCode, ][postalCode]</code> (default)</td>
          <td><code>123 Main St, Suite 200, Ottawa, ON, K1A 0B1</code></td>
        </tr>
        <tr>
          <td><code>[Address1, ][Address2, ]City[, StateCode][ PostalCode]</code></td>
          <td><code>123 Main St, Suite 200, Ottawa, ON K1A 0B1</code></td>
        </tr>
        <tr>
          <td><code>[Address1, ][Address2, ]City[, StateCode]</code></td>
          <td><code>123 Main St, Suite 200, Ottawa, ON</code></td>
        </tr>
        <tr>
          <td><code>Attention - Address1, City, StateCode PostalCode</code></td>
          <td><code>Front Desk - 123 Main St, Ottawa, ON K1A 0B1</code></td>
        </tr>
        <tr>
          <td><code><code>&#96;&#36;&#123;FacilityName&#125;[ &ndash; City, StateName]&#96;</code>
          </code></td>
          <td><code>Pineview Golf Club – Ottawa, Ontario</code></td>
        </tr>
        </tbody>
      </table>
      <h3>AddressUtils convenience methods</h3>
      <table class="table">
        <thead><tr><th>Method</th><th>Format</th><th>Example output</th></tr></thead>
        <tbody>
        <tr>
          <td><code>AddressUtils.formatAddressToString(address)</code></td>
          <td><code>[Address1, ][Address2, ]City[, StateCode][ PostalCode]</code></td>
          <td><code>123 Main St, Suite 200, Ottawa, ON K1A 0B1</code></td>
        </tr>
        <tr>
          <td><code>AddressUtils.formatAddressToSlightlySmallerString(address)</code></td>
          <td><code>[Address1, ][Address2, ]City[, StateCode]</code></td>
          <td><code>123 Main St, Suite 200, Ottawa, ON</code></td>
        </tr>
        <tr>
          <td><code>AddressUtils.formatFacilityAddressToLocationString(address, 'Club Name')</code></td>
          <td><code>ClubName[ – City, StateName]</code></td>
          <td><code>Club Name – Ottawa, Ontario</code></td>
        </tr>
        </tbody>
      </table>
    </div>

    <h2>GetFileSize Pipe</h2>
    <div>Path: <code class="component-path">./get-file-size.pipe.ts</code></div>
    <div>Pipe name: <code>getFileSize</code></div>
    <div>Usage: <code>file | getFileSize</code></div>
    <div class="gallery-item">
      <p>Converts a <code>File</code> object's byte size to a human-readable KB or MB string.</p>
      <table class="table">
        <thead><tr><th>Input (File.size)</th><th>Output</th></tr></thead>
        <tbody>
        <tr><td><code>512 000 bytes</code></td><td><code>512.00 KB</code></td></tr>
        <tr><td><code>1 500 000 bytes</code></td><td><code>1.50 MB</code></td></tr>
        <tr><td><code>null</code></td><td><code>0</code></td></tr>
        </tbody>
      </table>
    </div>

    <h2>LocalizedText Pipe</h2>
    <div>Path: <code class="component-path">./localized-text.pipe.ts</code></div>
    <div>Pipe name: <code>localizedText</code></div>
    <div>Usage: <code>englishText | localizedText:frenchText</code></div>
    <div class="gallery-item">
      <p>Returns the French string when the active locale is French, otherwise returns the English string.</p>
      <table class="table">
        <thead><tr><th>Call</th><th>Active locale</th><th>Output</th></tr></thead>
        <tbody>
        <tr><td><code>'Hello' | localizedText:'Bonjour'</code></td><td>en</td><td><code>{{ 'Hello' | localizedText: 'Bonjour'}}</code></td></tr>
        <tr><td><code>'Hello' | localizedText:'Bonjour'</code></td><td>fr</td><td><code>{{  localizedTextService.getLocalizedText('Hello', 'Bonjour')}}</code></td></tr>
        <tr><td><code>'Hello' | localizedText</code></td><td>fr</td><td><code>{{  localizedTextService.getLocalizedText('Hello', null)}}</code> (no French provided)</td></tr>
        </tbody>
      </table>
    </div>
  </section>

  <!-- ===== UTILITIES ===== -->
  <h2 class="mt-4">Utilities</h2>
  <div>Path: <code class="component-path">src/lib/shared/utils</code></div>

  <section class="component-section">
    <h2>DateUtils</h2>
    <div>Path: <code class="component-path">./date-utils.ts</code></div>
    <div class="gallery-item">
      <table class="table">
        <thead><tr><th>Method</th><th>Input</th><th>Output</th></tr></thead>
        <tbody>
        <tr>
          <td><code>isoToDateString(iso)</code></td>
          <td><code>'2023-05-15T12:00:00Z'</code></td>
          <td><code>May 15, 2023</code></td>
        </tr>
        <tr>
          <td><code>isoToShorthandDateString(iso)</code></td>
          <td><code>'2023-05-15T12:00:00Z'</code></td>
          <td><code>May 15, 2023</code></td>
        </tr>
        <tr>
          <td><code>isoToShorthandDateTimeString(iso)</code></td>
          <td><code>'2023-05-15T15:30:00Z'</code></td>
          <td><code>May 15, 2023 at 3:30 PM</code></td>
        </tr>
        <tr>
          <td><code>dateToFullDateString(date)</code></td>
          <td><code>new Date(Date.UTC(2023,4,15))</code></td>
          <td><code>May 15, 2023</code></td>
        </tr>
        <tr>
          <td><code>dateToInputDateString(date)</code></td>
          <td><code>new Date(Date.UTC(2023,4,15))</code></td>
          <td><code>15/05/2023</code></td>
        </tr>
        <tr>
          <td><code>dateToIsoDateOnly(date)</code></td>
          <td><code>new Date(Date.UTC(2023,4,15))</code></td>
          <td><code>2023-05-15</code></td>
        </tr>
        <tr>
          <td><code>ngbDateToDate(ngbDate)</code></td>
          <td><code>NgbDate(2023, 5, 15)</code></td>
          <td><code>Date at UTC 2023-05-15</code></td>
        </tr>
        <tr>
          <td><code>dateToNgbDate(date)</code></td>
          <td><code>new Date(Date.UTC(2023,4,15))</code></td>
          <td><code>NgbDate(2023, 5, 15)</code></td>
        </tr>
        <tr>
          <td><code>startOfDate(date)</code></td>
          <td><code>2023-05-15T15:30:00Z</code></td>
          <td><code>2023-05-15T00:00:00Z</code></td>
        </tr>
        <tr>
          <td><code>endOfDate(date)</code></td>
          <td><code>2023-05-15T15:30:00Z</code></td>
          <td><code>2023-05-15T23:59:59.999Z</code></td>
        </tr>
        <tr>
          <td><code>addDaysToUtcDateOnly(dateOnly, days)</code></td>
          <td><code>'2023-05-15', 3</code></td>
          <td><code>'2023-05-18'</code></td>
        </tr>
        <tr>
          <td><code>formatDateRange(from, to)</code></td>
          <td><code>'2026-02-01', '2026-02-04'</code></td>
          <td><code>February 1–4, 2026</code></td>
        </tr>
        </tbody>
      </table>
    </div>

    <h2>AddressUtils</h2>
    <div>Path: <code class="component-path">./address-utils.ts</code></div>
    <div class="gallery-item">
      <p>See the <strong>Address Pipe</strong> section above for full token reference and format examples.</p>
      <p>Key static methods: <code>format(address, formatString)</code>, <code>formatAddressToString</code>, <code>formatAddressToSlightlySmallerString</code>, <code>formatFacilityAddressToLocationString</code>.</p>
    </div>

    <h2>PhoneNumberUtils</h2>
    <div>Path: <code class="component-path">./phone-number-utils.ts</code></div>
    <div class="gallery-item">
      <table class="table">
        <thead><tr><th>Method</th><th>Input</th><th>Output</th></tr></thead>
        <tbody>
        <tr>
          <td><code>formatFullPhoneNumber(phoneNumber)</code></td>
          <td><code>'16135551234'</code> (11 digits)</td>
          <td><code>+1 613-555-1234</code></td>
        </tr>
        <tr>
          <td><code>formatFullPhoneNumber(phoneNumber)</code></td>
          <td><code>'441614961234'</code> (12 digits)</td>
          <td><code>+44 161-496-1234</code></td>
        </tr>
        <tr>
          <td><code>formatShortPhoneNumber(phoneNumber)</code></td>
          <td><code>'6135551234'</code> (10 digits)</td>
          <td><code>613-555-1234</code></td>
        </tr>
        <tr>
          <td><code>formatFullPhoneNumber(phoneNumber, countryCode)</code></td>
          <td><code>'16135551234', 'CA'</code></td>
          <td><code>CA +1 613-555-1234</code></td>
        </tr>
        <tr>
          <td><code>trimCountryCode(phoneNumber, countryCode, countries)</code></td>
          <td><code>'+16135551234', 'CA', countries</code></td>
          <td>Phone number with the country calling code stripped</td>
        </tr>
        </tbody>
      </table>
      <h3>Maskito Mask Patterns</h3>
      <table class="table">
        <thead><tr><th>Property</th><th>Digit count</th><th>Pattern</th></tr></thead>
        <tbody>
        <tr><td><code>maskitoOptions10Digit</code></td><td>10</td><td><code>XXX-XXX-XXXX</code></td></tr>
        <tr><td><code>maskitoOptions11Digit</code></td><td>11</td><td><code>+X XXX-XXX-XXXX</code></td></tr>
        <tr><td><code>maskitoOptions12Digit</code></td><td>12</td><td><code>+XX XXX-XXX-XXXX</code></td></tr>
        <tr><td><code>maskitoOptions13Digit</code></td><td>13</td><td><code>+XXX XXX-XXX-XXXX</code></td></tr>
        </tbody>
      </table>
    </div>

    <h2>FileUtils</h2>
    <div>Path: <code class="component-path">./file-utils.ts</code></div>
    <div class="gallery-item">
      <table class="table">
        <thead><tr><th>Method</th><th>Description</th><th>Example</th></tr></thead>
        <tbody>
        <tr>
          <td><code>fileToB64(file)</code></td>
          <td>Converts a <code>File</code> to a base64 data URL as an <code>Observable&lt;string&gt;</code></td>
          <td><code>FileUtils.fileToB64(file).subscribe(b64 => ...)</code></td>
        </tr>
        <tr>
          <td><code>getFileSizeString(bytes)</code></td>
          <td>Formats a byte count as a KB or MB string</td>
          <td><code>getFileSizeString(1_500_000)</code> → <code>1.50 MB</code></td>
        </tr>
        <tr>
          <td><code>getAttachmentTypeFromFile(file)</code></td>
          <td>Returns <code>AttachmentTypeId</code> based on MIME type</td>
          <td>PNG/JPEG → <code>Image</code> · PDF → <code>PDF</code> · other → <code>Doc</code></td>
        </tr>
        <tr>
          <td><code>openFileInNewTab(file)</code></td>
          <td>Opens the file in a new browser tab using a Blob URL</td>
          <td><code>FileUtils.openFileInNewTab(file)</code></td>
        </tr>
        </tbody>
      </table>
    </div>

    <h2>CsvUtils</h2>
    <div>Path: <code class="component-path">./csv-utils.ts</code></div>
    <div class="gallery-item">
      <table class="table">
        <thead><tr><th>Method</th><th>Description</th><th>Example</th></tr></thead>
        <tbody>
        <tr>
          <td><code>convertToCSV(objArray, headerList)</code></td>
          <td>Converts an array of objects to a CSV string with the given headers</td>
          <td><code>CsvUtils.convertToCSV([&#123;name:'Alice'&#125;], ['name'])</code> → <code>"name\r\nAlice\r\n"</code></td>
        </tr>
        <tr>
          <td><code>escapeCsvField(value)</code></td>
          <td>Wraps values containing commas, quotes, or newlines in double quotes and escapes internal quotes</td>
          <td><code>escapeCsvField('Hello, "World"')</code> → <code>"Hello, ""World"""</code></td>
        </tr>
        <tr>
          <td><code>downloadFile(data, headerList, filename?)</code></td>
          <td>Triggers a browser download of the data as a UTF-8 CSV file</td>
          <td><code>CsvUtils.downloadFile(rows, ['id','name'], 'members')</code></td>
        </tr>
        </tbody>
      </table>
    </div>

    <h2>HtmlUtils</h2>
    <div>Path: <code class="component-path">./html-utils.ts</code></div>
    <div class="gallery-item">
      <table class="table">
        <thead><tr><th>Method</th><th>Description</th><th>Example</th></tr></thead>
        <tbody>
        <tr>
          <td><code>countVisibleCharacters(html)</code></td>
          <td>Strips all HTML tags and counts the visible text characters (used for rich-text max-length validation)</td>
          <td><code>HtmlUtils.countVisibleCharacters('&lt;p&gt;Hello&lt;/p&gt;')</code> → <code>5</code></td>
        </tr>
        </tbody>
      </table>
    </div>

    <h2>DialogUtils</h2>
    <div>Path: <code class="component-path">./dialog-utils.ts</code></div>
    <div class="gallery-item">
      <p>Returns pre-configured <code>MatDialogConfig</code> objects. All sizes set <code>disableClose: true</code> by default.</p>
      <table class="table">
        <thead><tr><th>Method</th><th>Width</th><th>Usage</th></tr></thead>
        <tbody>
        <tr>
          <td><code>DialogUtils.smallDialogConfig(options?)</code></td>
          <td>600 px</td>
          <td><code>this.dialog.open(MyComponent, DialogUtils.smallDialogConfig())</code></td>
        </tr>
        <tr>
          <td><code>DialogUtils.mediumDialogConfig(options?)</code></td>
          <td>850 px</td>
          <td><code>this.dialog.open(MyComponent, DialogUtils.mediumDialogConfig())</code></td>
        </tr>
        <tr>
          <td><code>DialogUtils.largeDialogConfig(options?)</code></td>
          <td>1 200 px</td>
          <td><code>this.dialog.open(MyComponent, DialogUtils.largeDialogConfig())</code></td>
        </tr>
        </tbody>
      </table>
    </div>

    <h2>FormValidationUtils</h2>
    <div>Path: <code class="component-path">./form-validation-utils.ts</code></div>
    <div class="gallery-item">
      <p>Provides shared default form-error message maps.</p>
      <table class="table">
        <thead><tr><th>Constant</th><th>Value</th></tr></thead>
        <tbody>
        <tr>
          <td><code>FormValidationUtils.DEFAULT_FORM_ERROR_MESSAGES</code></td>
          <td><code>&#123; required: 'Required' &#125;</code></td>
        </tr>
        </tbody>
      </table>
      <p>Usage: <code>[errorMessages]="FormValidationUtils.DEFAULT_FORM_ERROR_MESSAGES"</code></p>
    </div>

    <h2>AttachmentUtils</h2>
    <div>Path: <code class="component-path">./attachment-utils.ts</code></div>
    <div class="gallery-item">
      <table class="table">
        <thead><tr><th>Function</th><th>Description</th><th>Example</th></tr></thead>
        <tbody>
        <tr>
          <td><code>attachmentToFile(attachment)</code></td>
          <td>Fetches an <code>Attachment</code>'s <code>originalLink</code> URL and converts it to a <code>File</code> object as an <code>Observable&lt;File | null&gt;</code></td>
          <td><code>attachmentToFile(attachment).subscribe(file =&gt; ...)</code></td>
        </tr>
        </tbody>
      </table>
    </div>
  </section>
</div>

<div class="gallery-container">
  <div class="gallery-header">
    <h1>Shared Components gallery</h1>
    <p>Interactive gallery of all shared library components</p>
  </div>

  <h1>Collapsible Content</h1>
  <div>
    Path:
    <code class="component-path">src/lib/shared/components/collapsible-content</code>
  </div>
  <section class="component-section">
    <div>Selector: <code>&lt;lib-collapsible-content&gt;</code></div>
    <div class="gallery-item">
      <lib-collapsible-content
        [collapsed]="false"
        titleText="Section 1 - Expanded">
        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer posuere, enim at feugiat aliquam, justo
        nunc consequat arcu, sed luctus eros magna in nibh. Mauris porta, risus vel pulvinar ultricies, lacus velit
        ullamcorper velit, eu tincidunt turpis nibh ac ipsum. Sed sit amet semper turpis. Cras gravida, nisl id
        fermentum venenatis, purus nunc feugiat sem, et sagittis risus tortor sed lorem. Vestibulum ante ipsum primis
        in faucibus orci luctus et ultrices posuere cubilia curae; Nam suscipit est id ipsum pellentesque, nec
        suscipit mauris tincidunt.
      </lib-collapsible-content>

      <lib-collapsible-content
        [collapsed]="true"
        titleText="Section 2 - Collapsed">
        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Praesent commodo augue nec nisl malesuada, eu
        dignissim nisl eleifend. Curabitur non ante sed nibh posuere cursus. Vestibulum in justo id augue congue
        sollicitudin. Aenean tristique velit non tellus tristique, vitae luctus lectus aliquet. Nullam eu tempor
        tellus. Quisque vitae libero eu nisi congue tincidunt in non ante. Etiam id nibh quis magna pulvinar suscipit
        ac vitae ligula.
      </lib-collapsible-content>

      <lib-collapsible-content
        [collapseIconPosition]="CollapsibleContentIconPosition.left"
        [collapsed]="true"
        titleText="Section 3 - Collapsed - Left">
        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce semper lacinia lacus, nec posuere est
        convallis at. Aliquam placerat tortor quis lectus varius, eget feugiat ligula ullamcorper. Donec molestie
        mauris ac diam iaculis hendrerit. Aenean feugiat commodo orci, in volutpat sem dictum id. Etiam et malesuada
        nibh, at feugiat sapien. Integer mattis, neque vitae gravida elementum, lectus massa malesuada mi, vitae
        vulputate velit metus ut eros.
      </lib-collapsible-content>

      <lib-collapsible-content
        [collapseIconPosition]="CollapsibleContentIconPosition.left"
        [collapsed]="false"
        titleText="Section 4 - Expanded - Left">
        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce semper lacinia lacus, nec posuere est
        convallis at. Aliquam placerat tortor quis lectus varius, eget feugiat ligula ullamcorper. Donec molestie
        mauris ac diam iaculis hendrerit. Aenean feugiat commodo orci, in volutpat sem dictum id. Etiam et malesuada
        nibh, at feugiat sapien. Integer mattis, neque vitae gravida elementum, lectus massa malesuada mi, vitae
        vulputate velit metus ut eros.
      </lib-collapsible-content>
    </div>

  </section>

  <h1>Buttons</h1>
  <div>Path: <code class="component-path">src/lib/shared/components/buttons</code></div>
  <section class="component-section">
    <h2>Buttons</h2>
    <div>Path: <code class="component-path">./button</code></div>
    <div>Selector: <code>&lt;lib-button&gt;</code></div>
    <div class="gallery-item">
      <h3>Large Button Types</h3>
      <div class="component-preview buttons-grid">
        <lib-button (buttonClick)="onButtonClick('Primary')" [type]="ButtonType.primary"> Primary Button</lib-button>
        <lib-button (buttonClick)="onButtonClick('Secondary')" [type]="ButtonType.secondary">
          Secondary Button
        </lib-button>
        <lib-button (buttonClick)="onButtonClick('Tertiary')" [type]="ButtonType.tertiary">
          Tertiary Button
        </lib-button>
        <lib-button [disabled]="true" [type]="ButtonType.primary"> Disabled Button</lib-button>
        <lib-button [loading]="true" [type]="ButtonType.primary"> Loading Button</lib-button>
        <lib-button [loading]="true" [type]="ButtonType.secondary"> Loading Button</lib-button>
        <lib-button
          (buttonClick)="onButtonClick('Primary with icon')"
          [type]="ButtonType.primary"
          leadingIconName="add">
          With Icon
        </lib-button>
        <lib-button
          (buttonClick)="onButtonClick('Secondary with icon')"
          [type]="ButtonType.secondary"
          trailingIconName="chevron-right">
          With Trailing Icon
        </lib-button>
      </div>
    </div>
    <div class="gallery-item">
      <h3>Small Button Types</h3>
      <div class="component-preview buttons-grid">
        <lib-button (buttonClick)="onButtonClick('Primary')" [size]="InputSize.small" [type]="ButtonType.primary">
          Primary Button
        </lib-button>
        <lib-button (buttonClick)="onButtonClick('Secondary')" [size]="InputSize.small" [type]="ButtonType.secondary">
          Secondary Button
        </lib-button>
        <lib-button (buttonClick)="onButtonClick('Tertiary')" [size]="InputSize.small" [type]="ButtonType.tertiary">
          Tertiary Button
        </lib-button>
        <lib-button [disabled]="true" [size]="InputSize.small" [type]="ButtonType.primary"> Disabled Button</lib-button>
        <lib-button [loading]="true" [size]="InputSize.small" [type]="ButtonType.primary"> Loading Button</lib-button>
        <lib-button [loading]="true" [size]="InputSize.small" [type]="ButtonType.secondary"> Loading Button</lib-button>
        <lib-button
          (buttonClick)="onButtonClick('Primary with icon')" [size]="InputSize.small"
          [type]="ButtonType.primary"
          leadingIconName="add">
          With Icon
        </lib-button>
        <lib-button
          (buttonClick)="onButtonClick('Secondary with icon')" [size]="InputSize.small"
          [type]="ButtonType.secondary"
          trailingIconName="chevron-right">
          With Trailing Icon
        </lib-button>
      </div>
    </div>

    <h2>Dual Toggle</h2>
    <div>Path: <code class="component-path">./dual-toggle</code></div>
    <div>Selector: <code>&lt;lib-dual-toggle&gt;</code></div>
    <div class="gallery-item">
      <h3>Text</h3>
      <div class="component-preview">
        <lib-dual-toggle
          (toggleSelect)="onDualToggleTextChange($event)"
          [selected]="dualToggleTextState()"
          type="text"
          left="Percentage"
          right="Dollars">
        </lib-dual-toggle>
      </div>
      <div class="state-info">
        Current:
        {{ dualToggleTextState() === DualToggleState.left ? 'Left' : 'Right' }}
      </div>
    </div>
    <div class="gallery-item">
      <h3>Compact Text</h3>
      <div class="component-preview">
        <lib-dual-toggle
          (toggleSelect)="onDualToggleCompactTextChange($event)"
          [selected]="dualToggleCompactTextState()"
          type="compact-text"
          left="EN"
          right="FR">
        </lib-dual-toggle>
      </div>
      <div class="state-info">
        Current:
        {{ dualToggleCompactTextState() === DualToggleState.left ? 'Left' : 'Right' }}
      </div>
    </div>
    <div class="gallery-item">
      <h3>Icons</h3>
      <div class="component-preview">
        <lib-dual-toggle
          (toggleSelect)="onDualToggleIconsChange($event)"
          [selected]="dualToggleIconsState()"
          type="symbol"
          left="location"
          right="map">
        </lib-dual-toggle>
      </div>
      <div class="state-info">
        Current:
        {{ dualToggleIconsState() === DualToggleState.left ? 'Left' : 'Right' }}
      </div>
    </div>

    <h2>Button Dropdown</h2>
    <div class="gallery-item">
      <div>Path: <code class="component-path">./button-dropdown</code></div>
      <div>Selector: <code>&lt;lib-button-dropdown&gt;</code></div>
      <div class="component-preview">
        <lib-button-dropdown (itemSelected)="onDropdownItemSelected()"> Dropdown Button</lib-button-dropdown>
      </div>
    </div>

    <h2>Filter Button</h2>
    <div>Path: <code class="component-path">./filter-button</code></div>
    <div>Selector: <code>&lt;lib-filter-button&gt;</code></div>
    <div class="gallery-item">
      <div class="component-preview">
        <lib-filter-button (buttonClick)="onFilterClick()" [badgeCount]="filterBadgeCount()">
          Filters
        </lib-filter-button>
      </div>
    </div>
  </section>

  <h1>Content Header</h1>
  <div>
    Path:
    <code class="component-path">src/lib/shared/components/content-header</code>
  </div>
  <section class="component-section">
    <div>Selector: <code>&lt;lib-content-header&gt;</code></div>
    <div class="gallery-item">
      <h3>Basic Content Header</h3>
      <div class="component-preview">
        <lib-content-header>Page Title</lib-content-header>
      </div>
    </div>
  </section>

  <h1>Interactive Labels</h1>
  <div>
    Path:
    <code class="component-path">src/lib/shared/components/interactive-labels</code>
  </div>
  <section class="component-section">
    <h2>Radio Buttons</h2>
    <div>Path: <code class="component-path">./radio-button</code></div>
    <div>Selector: <code>&lt;lib-radio-button&gt;</code></div>
    <div class="gallery-item">
      <h3>Radio Group</h3>
      <div class="component-preview">
        <div class="radio-group">
          <lib-radio-button (buttonClick)="onRadio1Click()" [selected]="radioState1()"> Option 1</lib-radio-button>
          <lib-radio-button (buttonClick)="onRadio2Click()" [selected]="radioState2()"> Option 2</lib-radio-button>
        </div>
      </div>
      <div class="state-info">Selected: {{ radioState1() ? 'Option 1' : 'Option 2' }}</div>
    </div>

    <h2>Checkboxes</h2>
    <div>Path: <code class="component-path">./checkbox</code></div>
    <div>Selector: <code>&lt;lib-checkbox&gt;</code></div>
    <div class="gallery-item">
      <h3>Basic Checkboxes</h3>
      <div class="component-preview">
        <lib-checkbox (checkboxClick)="onCheckboxChange()" [selected]="checkboxState()"> Checkbox</lib-checkbox>

        <lib-checkbox [disabled]="true" [selected]="false"> Disabled checkbox</lib-checkbox>

        <lib-checkbox [disabled]="true" [selected]="true"> Disabled checked checkbox</lib-checkbox>

        <lib-checkbox [indeterminate]="true" [selected]="true">Indeterminate checked checkbox</lib-checkbox>

        <lib-checkbox (checkboxClick)="onCheckboxChange()" [selected]="checkboxState()">Checkbox with a really long
          label, a label containing so much text in fact that it has to wrap onto the next line on most screen sizes,
          demonstrating the behaviour of the checkbox next to the label that it should drop down by 0.25rem when the
          second line of the label is added, but shouldn't drop down by any more than that once more than two lines of
          label are present.
        </lib-checkbox>
      </div>
      <div class="state-info">Checkbox State: {{ checkboxState() }}</div>
    </div>

    <h2>Toggle</h2>
    <div>Path: <code class="component-path">./toggle</code></div>
    <div>Selector: <code>&lt;lib-toggle&gt;</code></div>
    <div class="component-preview">
      <lib-toggle (toggleClick)="onToggleChange()" [selected]="toggleState()"> Toggle</lib-toggle>

      <lib-toggle [disabled]="true" [selected]="false"> Disabled Not Selected</lib-toggle>

      <lib-toggle [disabled]="true" [selected]="true"> Disabled Selected</lib-toggle>
    </div>

    <div class="state-info">State: {{ toggleState() }}</div>
  </section>

  <h1>Form</h1>
  <div>Path: <code class="component-path">src/lib/shared/components/form</code></div>
  <section class="component-section">
    <h2>Text Input</h2>
    <div>Path: <code class="component-path">./inputs/text-input</code></div>
    <div>Selector: <code>&lt;lib-text-input&gt;</code></div>
    <div class="gallery-item">
      <h3>Basic Inputs</h3>
      <div class="component-preview inputs-grid">
        <lib-text-input placeholder="Enter your name"></lib-text-input>
        <lib-text-input [error]="true" placeholder="Input with error"></lib-text-input>
        <lib-text-input [inputSize]="InputSize.small" placeholder="Small input"></lib-text-input>
        <lib-text-input [showClearButton]="true" placeholder="Show Clear Button"></lib-text-input>
      </div>
      <h3>Basic Inputs when they're disabled</h3>
      <div class="component-preview inputs-grid">
        <lib-text-form-field [inputFormControl]="disabledTextFormControl"
                             label="Large Text Field Input"
                             class="w-25"></lib-text-form-field>
      </div>
    </div>

    <h2>Postal Code Input</h2>
    <div>Path: <code class="component-path">./inputs/postal-code-input</code></div>
    <div>Selector: <code>&lt;lib-postal-code-input&gt;</code></div>
    <div class="gallery-item">
      <div class="mb-3" style="max-width: 200px;">
        <lib-dropdown-input
          [dropdownItems]="postalCodeCountryDropdownItems()"
          [formControl]="postalCodeCountryFormControl"
          placeholder="Select country">
        </lib-dropdown-input>
      </div>
      <div class="component-preview inputs-grid">
        <lib-postal-code-input [countryType]="postalCodeCountry()"></lib-postal-code-input>
        <lib-postal-code-input [countryType]="postalCodeCountry()" [error]="true"></lib-postal-code-input>
        <lib-postal-code-input [countryType]="postalCodeCountry()" [inputSize]="InputSize.small"></lib-postal-code-input>
      </div>
    </div>

    <h2>Postal Code Form Field</h2>
    <div>Path: <code class="component-path">./form-fields/postalcode-form-field</code></div>
    <div>Selector: <code>&lt;lib-postal-code-form-field&gt;</code></div>
    <div class="gallery-item">
      <div class="mb-3" style="max-width: 200px;">
        <lib-dropdown-input
          [dropdownItems]="postalCodeCountryDropdownItems()"
          [formControl]="postalCodeCountryFormControl"
          placeholder="Select country">
        </lib-dropdown-input>
      </div>
      <div class="component-preview inputs-grid">
        <lib-postal-code-form-field
          [label]="postalCodeCountry() === CountryType.Canada ? 'Postal Code' : 'ZIP Code'"
          [countryType]="postalCodeCountry()"
          [inputFormControl]="postalCodeFormControl"
          [isSubmitted]="true"
          class="w-25">
        </lib-postal-code-form-field>
      </div>
    </div>

    <h2>Loading Input</h2>
    <div>Path: <code class="component-path">./inputs/loading-input</code></div>
    <div>Selector: <code>&lt;lib-loading-input&gt;</code></div>
    <div class="gallery-item">
      <lib-loading-input></lib-loading-input>
    </div>

    <h2>Textarea Input</h2>
    <div>Path: <code class="component-path">./inputs/textarea-input</code></div>
    <div>Selector: <code>&lt;lib-textarea-input&gt;</code></div>
    <div class="gallery-item">
      <h3>Basic Inputs</h3>
      <div class="component-preview inputs-grid">
        <lib-textarea-input placeholder="Enter your name"></lib-textarea-input>
        <lib-textarea-input [error]="true" placeholder="Input with error"></lib-textarea-input>
        <lib-textarea-input [inputSize]="InputSize.small" placeholder="Small input"></lib-textarea-input>
        <lib-textarea-input [showClearButton]="true" placeholder="Show Clear Button"></lib-textarea-input>
      </div>
    </div>

    <h2>Currency Input</h2>
    <div>Path: <code class="component-path">./inputs/currency-input</code></div>
    <div>Selector: <code>&lt;lib-currency-input&gt;</code></div>
    <div class="gallery-item">
      <h3>Basic Inputs</h3>
      <div class="component-preview inputs-grid">
        <lib-currency-input placeholder="Currency input"></lib-currency-input>
        <lib-currency-input [error]="true" placeholder="Error for whatever reason"></lib-currency-input>
        <lib-currency-input [inputSize]="InputSize.small" placeholder="0.00"></lib-currency-input>
      </div>
    </div>

    <h2>Rate Input</h2>
    <div>Path: <code class="component-path">./inputs/rate-input</code></div>
    <div>Selector: <code>&lt;lib-rate-input&gt;</code></div>
    <div class="gallery-item">
      <h3>Basic Inputs</h3>
      <div class="component-preview inputs-grid">
        <lib-rate-input placeholder="Rate input"></lib-rate-input>
        <lib-rate-input [inputSize]="InputSize.large" placeholder="0.00"></lib-rate-input>
        <lib-rate-input [inputSize]="InputSize.small" placeholder="0.00"></lib-rate-input>
      </div>
    </div>

    <h2>Password Input</h2>
    <div>Path: <code class="component-path">./inputs/password-input</code></div>
    <div>Selector: <code>&lt;lib-password-input&gt;</code></div>
    <div class="gallery-item">
      <h3>Basic Inputs</h3>
      <div class="component-preview inputs-grid">
        <lib-password-form-field [inputFormControl]="passwordFormControl" [isSubmitted]="true" label="Password">
        </lib-password-form-field>
      </div>
    </div>

    <h2>Typeahead Input</h2>
    <div>Path: <code class="component-path">./inputs/typeahead-input</code></div>
    <div>Selector: <code>&lt;lib-typeahead-input&gt;</code></div>
    <div class="gallery-item">
      <div class="component-preview">
        <lib-typeahead-input (valueChange)="onTypeaheadValueChange($event)"
                             [initialSelectedName]="'Initial Selected Name'"
                             placeholder="Search...">
        </lib-typeahead-input>
      </div>
    </div>

    <h2>Dropdown Input</h2>
    <div>Path: <code class="component-path">./inputs/dropdown-input</code></div>
    <div>Selector: <code>&lt;lib-dropdown-input&gt;</code></div>
    <div class="gallery-item">
      <h3>Dropdown Input</h3>
      <div class="component-preview">
        <lib-dropdown-input [dropdownItems]="dropdownItems()" placeholder="Select an option"></lib-dropdown-input>
      </div>
    </div>

    <h2>Date Input</h2>
    <div>Path: <code class="component-path">./inputs/date-input</code></div>
    <div>Selector: <code>&lt;lib-date-input&gt;</code></div>
    <div class="gallery-item">
      <div class="component-preview">
        <lib-date-input minDate="2026-09-01" maxDate="2026-12-12" placeholder="Select date"></lib-date-input>
      </div>
    </div>

    <h2>Phone Input</h2>
    <div>Path: <code class="component-path">./inputs/phone-input</code></div>
    <div>Selector: <code>&lt;lib-phone-input&gt;</code></div>

    <div class="gallery-item">
      <div class="component-preview">
        <lib-phone-input [countryCodeFormControl]="phoneFormControl" placeholder="Enter phone number">
        </lib-phone-input>
      </div>
    </div>

    <h2>File Input Form Field</h2>
    <div>
      Path:
      <code class="component-path">./form-fields/file-upload-form-field</code>
    </div>
    <div>Selector: <code>&lt;lib-file-upload-form-field&gt;</code></div>

    <div class="gallery-item">
      <div class="component-preview w-100">
        <lib-file-upload-form-field
          [hintText]="'Optional'"
          [inputFormControl]="fileFormControl"
          [isSubmitted]="true"
          [label]="'File Upload'"
          class="mb-4 d-block w-100">
        </lib-file-upload-form-field>
        <div class="d-flex flex-column gap-3 w-100">
          @for (file of files(); track $index) {
            <lib-file-list-item
              [validationErrors]="FileUtils.isFileSizeValid(file) ? null : { maxFileSize: true }"
              [file]="file">
            </lib-file-list-item>
          }
        </div>
        <lib-button (buttonClick)="popCropModal()">Crop Images</lib-button>
      </div>
    </div>

    <h2>Rich Text Input Form Field</h2>
    <div>
      Path:
      <code class="component-path">./form-fields/rich-text-form-field</code>
    </div>
    <div>Selector: <code>&lt;lib-rich-text-form-field&gt;</code></div>

    <div class="gallery-item">
      <lib-rich-text-form-field
        [inputFormControl]="richTextFormControl"
        [isSubmitted]="true"
        [maxCharacters]="50"
        hintText="Optional"
        label="Label">
      </lib-rich-text-form-field>
      <lib-button (buttonClick)="toggleRichTextEnabled()" class="d-block mt-3">Toggle Enabled</lib-button>
    </div>

    <h2>Readonly Form Field</h2>
    <div>
      Path:
      <code class="component-path">./form-fields/readonly-form-field</code>
    </div>
    <div>Selector: <code>&lt;lib-readonly-form-field&gt;</code></div>

    <div class="gallery-item">
      <lib-readonly-form-field
        (trailingIconClick)="trailingIconClick()"
        label="Label"
        trailingIconName="new-window"
        value="Value">
      </lib-readonly-form-field>
    </div>

    <h2>Select All Checkbox Form Field</h2>
    <div>
      Path:
      <code class="component-path">./form-fields/select-all-checkbox-form-field</code>
    </div>
    <div>Selector: <code>&lt;lib-select-all-checkbox-form-field&gt;</code></div>

    <div class="gallery-item">
      <div class="multi-checkbox-container">
        <lib-select-all-checkbox-form-field
          [childCheckboxFormControls]="childCheckboxFormControls">
        </lib-select-all-checkbox-form-field>
        <div class="child-checkbox-container">
          <lib-checkbox-form-field [formControl]="child1CheckboxFormControl"
                                   label="Child 1"></lib-checkbox-form-field>
          <lib-checkbox-form-field [formControl]="child2CheckboxFormControl"
                                   label="Child 2"></lib-checkbox-form-field>
          <lib-checkbox-form-field [formControl]="child3CheckboxFormControl"
                                   label="Child 3"></lib-checkbox-form-field>
          <lib-checkbox-form-field [formControl]="child4CheckboxFormControl"
                                   label="Child 4"></lib-checkbox-form-field>
        </div>
      </div>
    </div>
  </section>

  <h1>Banners & Alerts</h1>
  <div>Path: <code class="component-path">src/lib/shared/components/banners</code></div>
  <section class="component-section">
    <h2>Banner Large</h2>
    <div>Path: <code class="component-path">./large-banner</code></div>
    <div>Selector: <code>&lt;lib-large-banner&gt;</code></div>
    <div [style.display]="'flex'" [style.flex-direction]="'column'" [style.gap]="'1rem'" class="gallery-item">
      <lib-large-banner [config]="{
        title: 'Join us for the 2025 Annual General Metting!! Info',
        icon: 'graph-arrow',
        description: 'secure your spot by completing the registration process before 2025-12-29. dont miss this opportunity to engage share ideas and stay informed about the organizations progress and initiatives',
        style: BannerStyle.Info,
        closeButtonClicked: bannerCloseButtonClicked,
        buttons: [{
          title: 'Label',
          leadingIconName: 'add',
          trailingIconName: 'chevron-down',
          style: ButtonType.secondary,
          onClick: bannerButtonClicked,
        },{
          title: 'Label',
          leadingIconName: 'add',
          trailingIconName: 'chevron-down',
          style: ButtonType.primary,
          onClick: bannerButtonClicked,
        }],
      }"/>
      <lib-large-banner [config]="{
        icon: 'graph-arrow',
        description: 'Error secure your spot by completing the registration process before 2025-12-29. dont miss this opportunity to engage share ideas and stay informed about the organizations progress and initiatives',
        style: BannerStyle.Error,
        closeButtonClicked: bannerCloseButtonClicked,
        buttons: [{
          title: 'Label',
          leadingIconName: 'add',
          trailingIconName: 'chevron-down',
          style: ButtonType.tertiary,
          onClick: bannerButtonClicked,
        },{
          title: 'Label',
          leadingIconName: 'add',
          trailingIconName: 'chevron-down',
          style: ButtonType.secondary,
          onClick: bannerButtonClicked,
        },{
          title: 'Label',
          leadingIconName: 'add',
          trailingIconName: 'chevron-down',
          style: ButtonType.primary,
          onClick: bannerButtonClicked,
        }],
      }"/>
      <lib-large-banner [config]="{
        title: 'Join us for the 2025 Annual General Metting!! Warning',
        description: 'secure your spot by completing the registration process before 2025-12-29. dont miss this opportunity to engage share ideas and stay informed about the organizations progress and initiatives',
        style: BannerStyle.Warning,
        buttons: [],
      }"/>
      <lib-large-banner [config]="{
        title: 'Join us for the 2025 Annual General Metting!! Success',
        icon: 'add',
        style: BannerStyle.Success,
        buttons: [{
          title: 'Label',
          style: ButtonType.secondary,
          onClick: bannerButtonClicked,
        },{
          title: 'Label',
          style: ButtonType.primary,
          onClick: bannerButtonClicked,
        }],
      }"/>
    </div>

    <h2>Banner Small</h2>
    <div>Path: <code class="component-path">./small-banner</code></div>
    <div>Selector: <code>&lt;lib-small-banner&gt;</code></div>
    <div [style.display]="'flex'" [style.flex-direction]="'column'" [style.gap]="'1rem'" class="gallery-item">
      <lib-small-banner [config]="{
        title: 'Join us for the 2025 Annual General Metting!! Info',
        icon: 'graph-arrow',
        style: BannerStyle.Info,
        closeButtonClicked: bannerCloseButtonClicked,
        buttons: [{
          title: 'Label',
          leadingIconName: 'add',
          trailingIconName: 'chevron-down',
          style: ButtonType.secondary,
          onClick: bannerButtonClicked,
        },{
          title: 'Label',
          leadingIconName: 'add',
          trailingIconName: 'chevron-down',
          style: ButtonType.primary,
          onClick: bannerButtonClicked,
        }],
      }"/>
      <lib-small-banner [config]="{
        icon: 'error',
        style: BannerStyle.Error,
        closeButtonClicked: bannerCloseButtonClicked,
        buttons: [{
          title: 'Label',
          leadingIconName: 'add',
          trailingIconName: 'chevron-down',
          style: ButtonType.tertiary,
          onClick: bannerButtonClicked,
        },{
          title: 'Label',
          leadingIconName: 'add',
          trailingIconName: 'chevron-down',
          style: ButtonType.secondary,
          onClick: bannerButtonClicked,
        },{
          title: 'Label',
          leadingIconName: 'add',
          trailingIconName: 'chevron-down',
          style: ButtonType.primary,
          onClick: bannerButtonClicked,
        }],
      }"/>
      <lib-small-banner [config]="{
        title: 'Join us for the 2025 Annual General Metting!! Warning',
        style: BannerStyle.Warning,
        buttons: [],
      }"/>
      <lib-small-banner [config]="{
        title: 'Join us for the 2025 Annual General Metting!! Success',
        icon: 'graph-arrow',
        style: BannerStyle.Success,
        buttons: [{
          title: 'Label',
          style: ButtonType.secondary,
          onClick: bannerButtonClicked,
        },{
          title: 'Label',
          style: ButtonType.primary,
          onClick: bannerButtonClicked,
        }],
      }"/>
      <lib-small-banner [config]="{
        title: 'Join us for the 2025 Annual General Metting!! Info',
        icon: 'graph-arrow',
        description: 'secure your spot by completing the registration process before 2025-12-29. dont miss this opportunity to engage share ideas and stay informed about the organizations progress and initiatives',
        style: BannerStyle.Info,
        closeButtonClicked: bannerCloseButtonClicked,
        buttons: [{
          title: 'Label',
          leadingIconName: 'add',
          trailingIconName: 'chevron-down',
          style: ButtonType.secondary,
          onClick: bannerButtonClicked,
        },{
          title: 'Label',
          leadingIconName: 'add',
          trailingIconName: 'chevron-down',
          style: ButtonType.primary,
          onClick: bannerButtonClicked,
        }],
      }"/>
      <lib-small-banner [config]="{
        icon: 'graph-arrow',
        description: 'Error secure your spot by completing the registration process before 2025-12-29. dont miss this opportunity to engage share ideas and stay informed about the organizations progress and initiatives',
        style: BannerStyle.Error,
        closeButtonClicked: bannerCloseButtonClicked,
        buttons: [{
          title: 'Label',
          leadingIconName: 'add',
          trailingIconName: 'chevron-down',
          style: ButtonType.tertiary,
          onClick: bannerButtonClicked,
        },{
          title: 'Label',
          leadingIconName: 'add',
          trailingIconName: 'chevron-down',
          style: ButtonType.secondary,
          onClick: bannerButtonClicked,
        },{
          title: 'Label',
          leadingIconName: 'add',
          trailingIconName: 'chevron-down',
          style: ButtonType.primary,
          onClick: bannerButtonClicked,
        }],
      }"/>
      <lib-small-banner [config]="{
        title: 'Join us for the 2025 Annual General Metting!! Warning',
        description: 'secure your spot by completing the registration process before 2025-12-29. dont miss this opportunity to engage share ideas and stay informed about the organizations progress and initiatives',
        style: BannerStyle.Warning,
        buttons: [],
      }"/>
      <lib-small-banner [config]="{
        title: 'Join us for the 2025 Annual General Metting!! Success',
        icon: 'graph-arrow',
        style: BannerStyle.Success,
        buttons: [{
          title: 'Label',
          style: ButtonType.secondary,
          onClick: bannerButtonClicked,
        },{
          title: 'Label',
          style: ButtonType.primary,
          onClick: bannerButtonClicked,
        }],
      }"/>
    </div>
  </section>

  <h1>Labels</h1>
  <div>Path: <code class="component-path">projects/shared-lib/src/lib/shared/components/labels</code></div>
  <section class="component-section">
    <h2>Progress Stepper</h2>
    <div>Path: <code class="component-path">./progress-stepper</code></div>
    <div>Selector: <code>&lt;lib-progress-stepper&gt;</code></div>
    <div class="gallery-item">
      <div class="component-preview">
        <lib-progress-stepper [steps]="progressStepperSteps" [currentStep]="currentStep()"></lib-progress-stepper>
      </div>
      <div class="d-flex">
        <lib-button [type]="ButtonType.tertiary" (buttonClick)="decreaseStep()" leadingIconName="minus"></lib-button>
        <lib-button [type]="ButtonType.tertiary" (buttonClick)="increaseStep()" leadingIconName="add"></lib-button>
      </div>
    </div>
    <h2>Info Labels</h2>
    <div>Path: <code class="component-path">./info-label</code></div>
    <div>Selector: <code>&lt;lib-info-label&gt;</code></div>
    <div class="gallery-item">
      <div class="row">
        <div class="d-flex flex-column gap-1 col-9">
          <lib-info-label leadingIconName="calendar" [size]="InfoLabelSize.default">December 9-12, 2024</lib-info-label>
          <lib-info-label leadingIconName="calendar" [size]="InfoLabelSize.large">December 9-12, 2024</lib-info-label>
          <lib-info-label leadingIconName="calendar" [size]="InfoLabelSize.small">December 9-12, 2024</lib-info-label>
        </div>
        <div class="d-flex flex-column gap-1 col-3">
          <lib-info-label leadingIconName="calendar" [size]="InfoLabelSize.default">Registrations for the championship:
            December 9-12, 2024
          </lib-info-label>
          <lib-info-label leadingIconName="calendar" [size]="InfoLabelSize.large">Registrations for the championship:
            December 9-12, 2024
          </lib-info-label>
          <lib-info-label leadingIconName="calendar" [size]="InfoLabelSize.small">Registrations for the championship:
            December 9-12, 2024
          </lib-info-label>
        </div>
      </div>
    </div>
  </section>

  <h1>Pills</h1>
  <div>Path: <code class="component-path">projects/shared-lib/src/lib/shared/components/pills</code></div>
  <section class="component-section">
    <h2>General Status Pill</h2>
    <div>Path: <code class="component-path">./general-status-pill</code></div>
    <div>Selector: <code>&lt;lib-general-status-pill&gt;</code></div>
    <div class="gallery-item d-flex flex-wrap gap-2">
      <lib-general-status-pill [type]="StatusPillType.grey">StatusPillType.grey</lib-general-status-pill>
      <lib-general-status-pill [type]="StatusPillType.green">StatusPillType.green</lib-general-status-pill>
      <lib-general-status-pill [type]="StatusPillType.red">StatusPillType.red</lib-general-status-pill>
      <lib-general-status-pill [type]="StatusPillType.yellow">StatusPillType.yellow</lib-general-status-pill>
      <lib-general-status-pill [type]="StatusPillType.blue">StatusPillType.blue</lib-general-status-pill>
      <lib-general-status-pill [type]="StatusPillType.neutral">StatusPillType.neutral</lib-general-status-pill>
      <lib-general-status-pill [type]="StatusPillType.error">StatusPillType.error</lib-general-status-pill>
      <lib-general-status-pill [type]="StatusPillType.purple">StatusPillType.purple</lib-general-status-pill>
      <lib-general-status-pill [type]="StatusPillType.forbidden">StatusPillType.forbidden</lib-general-status-pill>
      <lib-general-status-pill [type]="StatusPillType.lightGrey">StatusPillType.lightGrey</lib-general-status-pill>
      <lib-general-status-pill [type]="StatusPillType.error" [error]="true">[error]="true"</lib-general-status-pill>
    </div>
    <h2>Interactive Pill</h2>
    <div>Path: <code class="component-path">./interactive-pill</code></div>
    <div>Selector: <code>&lt;lib-interactive-pill&gt;</code></div>
    <div class="gallery-item d-flex flex-wrap gap-2">
      <lib-interactive-pill titleText="Selected" [selected]="true"></lib-interactive-pill>
      <lib-interactive-pill titleText="Deselected" [selected]="false"></lib-interactive-pill>
      <lib-interactive-pill titleText="Disabled & Selected" [selected]="true" [disabled]="true"></lib-interactive-pill>
      <lib-interactive-pill titleText="Disabled & Deselected"
                            [selected]="false"
                            [disabled]="true"></lib-interactive-pill>
      <lib-interactive-pill titleText="Show Deselect Icon & Selected"
                            [selected]="true"
                            [showCross]="true"></lib-interactive-pill>
      <lib-interactive-pill titleText="Show Deselect Icon & Deselected"
                            [selected]="false"
                            [showCross]="true"></lib-interactive-pill>
    </div>
  </section>

  <h1>Badges</h1>
  <div>Path: <code class="component-path">projects/shared-lib/src/lib/shared/components/badges</code></div>
  <section class="component-section">
    <h2>Date Badge</h2>
    <div>Path: <code class="component-path">./date-badge</code></div>
    <div>Selector: <code>&lt;lib-date-badge&gt;</code></div>
    <div class="gallery-item d-flex flex-wrap gap-2">
      <lib-date-badge [startDate]="startDate"></lib-date-badge>
      <lib-date-badge [startDate]="startDate" [endDate]="endDate"></lib-date-badge>
      <lib-date-badge [startDate]="startDate" [endDate]="endDateSpanMonth"></lib-date-badge>
      <lib-date-badge [startDate]="startDate" [endDate]="endDateSpanMonthYear"></lib-date-badge>
      <lib-date-badge [startDate]="startDate" [endDate]="endDateSpanYear"></lib-date-badge>
    </div>
  </section>

  <h1>Directives</h1>
  <div>Path: <code class="component-path">projects/shared-lib/src/lib/shared/directives</code></div>
  <section class="component-section">
    <h2>Repeat Directive</h2>
    <div>Path: <code class="component-path">./repeat.directive</code></div>
    <div>Selector: <code>*libRepeat</code></div>
    <div class="gallery-item">
      <div class="component-preview directive-repeat-preview">
        <div class="d-flex gap-2">
          <lib-button
            (buttonClick)="decreaseDirectiveRepeatCount()"
            [type]="ButtonType.tertiary"
            leadingIconName="minus">
          </lib-button>
          <lib-button
            (buttonClick)="increaseDirectiveRepeatCount()"
            [type]="ButtonType.tertiary"
            leadingIconName="add">
          </lib-button>
        </div>

        <div class="directive-repeat-items">
          <span class="directive-repeat-item" *libRepeat="repeatDirectiveCount(); let i = index">
            Item {{ i + 1 }}
          </span>
        </div>
      </div>
      <div class="state-info">Current repeat count: {{ repeatDirectiveCount() }}</div>
    </div>

    <h2>Tooltip Directive</h2>
    <div>Path: <code class="component-path">./tooltip.directive</code></div>
    <div>Selector: <code>[libTooltip]</code></div>
    <div class="gallery-item">
      <div class="component-preview">
        <button class="directive-demo-button" type="button" [libTooltip]="'Title style tooltip'">
          Hover me (title)
        </button>
        <button class="directive-demo-button" type="button" [libTooltip]="'Subtitle style tooltip'" [toolTipStyle]="'subtitle'">
          Hover me (subtitle)
        </button>
      </div>
    </div>

    <h2>Drag And Drop Directive</h2>
    <div>Path: <code class="component-path">./dnd-directive</code></div>
    <div>Selector: <code>[appDnd]</code></div>
    <div class="gallery-item">
      <div class="component-preview">
        <div appDnd class="directive-drop-zone" (fileDropped)="onDirectiveFileDropped($event)">
          Drag and drop files here
        </div>
        <lib-button (buttonClick)="clearDirectiveDroppedFiles()" [type]="ButtonType.tertiary">
          Clear dropped files
        </lib-button>
      </div>
      <div class="state-info">
        @if (directiveDroppedFileNames().length > 0) {
          Dropped files: {{ directiveDroppedFileNames().join(', ') }}
        } @else {
          No files dropped yet.
        }
      </div>
    </div>
  </section>
</div>



